import { z } from 'zod'

/* Spec PWA-033 — historico de atividade do padrinho (o que ele faz no app), lido pelo ERP.
   Lista fechada: acao fora daqui e recusada no servidor e no app. Cada acao tem a sua categoria.
   O app so manda navegacao e cliques em elementos marcados (CLIENT_ACTIVITY_ACTIONS); buscas, itens vistos e
   atualizacoes sao gravadas pelo servidor, na rota, depois do sucesso. */

export const ACTIVITY_CATEGORIES = ['navigation', 'click', 'search', 'update', 'view'] as const
export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number]

export const ACTIVITY_ACTION_CATEGORY = {
    // App (telas)
    'screen.viewed': 'navigation',
    'ui.click': 'click',
    // Servidor: itens vistos
    'catalog.child_viewed': 'view',
    'child.viewed': 'view',
    'child.timeline_viewed': 'view',
    'thread.opened': 'view',
    'app_sponsorship.viewed': 'view',
    // Servidor: buscas
    'catalog.search': 'search',
    'postal_code.lookup': 'search',
    // Servidor: atualizacoes
    'reservation.created': 'update',
    'reservation.cancelled': 'update',
    'sponsorship.created': 'update',
    'message.sent': 'update',
    'message.translated': 'update',
    'message.transcribed': 'update',
    'thread.read': 'update',
    'notice.read_all': 'update',
    'profile.updated': 'update',
    'contact.changed': 'update',
    'preferences.updated': 'update',
    'donation.started': 'update',
    'app_sponsorship.card_updated': 'update',
    'app_sponsorship.updated': 'update',
    'app_sponsorship.cancelled': 'update',
    'push.enabled': 'update',
    'push.disabled': 'update',
    // Servidor: o app passou do limite de eventos na hora (D-08)
    'activity.throttled': 'click',
} as const satisfies Record<string, ActivityCategory>

export type ActivityAction = keyof typeof ACTIVITY_ACTION_CATEGORY
export const ACTIVITY_ACTIONS = Object.keys(ACTIVITY_ACTION_CATEGORY) as [ActivityAction, ...ActivityAction[]]

/** Acoes que o app pode mandar em POST /activity. */
export const CLIENT_ACTIVITY_ACTIONS = ['screen.viewed', 'ui.click'] as const
export type ClientActivityAction = (typeof CLIENT_ACTIVITY_ACTIONS)[number]

export const ACTIVITY_LIMITS = { batch: 50, metadataKeys: 10, perHour: 600 } as const

/** So chaves de controle curtas (nomes de campo, filtros, contagens). Nunca valor digitado nem dado pessoal. */
export const activityMetadataSchema = z.record(z.string().min(1).max(40), z.union([z.string().max(80), z.number().finite(), z.boolean()]))
    .refine(m => Object.keys(m).length <= ACTIVITY_LIMITS.metadataKeys, { message: 'metadata com chaves demais' })
export type ActivityMetadata = z.infer<typeof activityMetadataSchema>

export const activityEventInputSchema = z.object({
    category: z.enum(ACTIVITY_CATEGORIES),
    action: z.enum(CLIENT_ACTIVITY_ACTIONS),
    /** Padrao da rota (/criancas/$childRef), nunca a URL com ids. */
    route: z.string().min(1).max(120).regex(/^\/[A-Za-z0-9/$_-]*$/).optional(),
    /** Id do item (childRef, threadId, protocolo). */
    target: z.string().min(1).max(80).regex(/^[A-Za-z0-9._:-]+$/).optional(),
    occurredAt: z.string().datetime(),
    visitId: z.string().uuid(),
    durationMs: z.number().int().nonnegative().max(24 * 60 * 60 * 1000).optional(),
    metadata: activityMetadataSchema.optional(),
}).strict().refine(e => ACTIVITY_ACTION_CATEGORY[e.action] === e.category, { message: 'categoria nao combina com a acao', path: ['category'] })
export type ActivityEventInput = z.infer<typeof activityEventInputSchema>

export const activityBatchSchema = z.object({
    events: z.array(activityEventInputSchema).min(1).max(ACTIVITY_LIMITS.batch),
}).strict()
export type ActivityBatch = z.infer<typeof activityBatchSchema>
